import React from "react";
import { ChevronLeft, Bell, Sparkles, Zap, Moon, ShieldCheck, Thermometer } from "lucide-react";

/**
 * AlertsOptIn — "Coming Soon" placeholder.
 *
 * The full 10DLC-compliant double-opt-in flow (phone → verify → prefs)
 * exists in git history; when Twilio approves the campaign we'll swap
 * this back to the working component. Until then this page communicates
 * status honestly and previews the alert catalog so users know what's
 * coming.
 */
const PREVIEW_ALERTS = [
  {
    icon: ShieldCheck,
    color: "#39FF14",
    label: "Warranty threshold risk",
    desc: "Ping when your SoH is on track to drop below the warranty floor before the mile / year cutoff.",
  },
  {
    icon: Zap,
    color: "#00E5FF",
    label: "Charging anomaly",
    desc: "Dropped-power sessions, early auto-stops, or unusual charge-limit shifts.",
  },
  {
    icon: Moon,
    color: "#7FD1FF",
    label: "Phantom drain uptick",
    desc: "Sudden increase in parked-drain %/day vs your 14-day baseline.",
  },
  {
    icon: Thermometer,
    color: "#FFB020",
    label: "Extreme heat / cold warning",
    desc: "Local weather crossing the pack-safe threshold for charging or long-park.",
  },
];

export const AlertsOptIn = ({ back }) => (
  <div className="space-y-4 pb-4" data-testid="mobile-app-view-alerts">
    {/* Header row */}
    <div className="flex items-center gap-3 py-1">
      <button
        type="button"
        onClick={back}
        data-testid="alerts-back"
        aria-label="Back"
        className="w-8 h-8 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/5"
      >
        <ChevronLeft className="w-4 h-4 text-white" strokeWidth={2.5} aria-hidden="true" />
      </button>
      <div>
        <div className="text-sm text-white font-semibold leading-tight">SMS Alerts</div>
        <div className="text-[10px] font-mono text-zinc-400">Coming soon</div>
      </div>
    </div>

    {/* Hero card — the "coming soon" callout itself */}
    <div
      className="rounded-2xl border p-5 text-center overflow-hidden relative"
      style={{
        borderColor: "rgba(0,229,255,0.28)",
        background:
          "linear-gradient(160deg, rgba(0,229,255,0.10) 0%, rgba(57,255,20,0.06) 100%)",
      }}
      data-testid="alerts-coming-soon-hero"
    >
      <div
        className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-3"
        style={{
          background: "rgba(0,229,255,0.14)",
          border: "1.5px solid rgba(0,229,255,0.55)",
        }}
      >
        <Bell className="w-6 h-6" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
      </div>
      <div
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 mb-2"
        style={{
          background: "rgba(0,229,255,0.14)",
          border: "1px solid rgba(0,229,255,0.35)",
        }}
      >
        <Sparkles className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "#00E5FF" }}>
          Coming soon
        </span>
      </div>
      <h2 className="font-display text-lg text-white font-semibold leading-tight max-w-[260px] mx-auto">
        Battery alerts, straight to your phone.
      </h2>
      <p className="mt-2 text-[12px] text-zinc-300 leading-relaxed max-w-[280px] mx-auto">
        We're finalizing 10DLC carrier approval for EVIQ's SMS gateway. Once
        live, opt-in takes 30 seconds and you'll only hear from us when
        something actually matters.
      </p>
    </div>

    {/* Preview of the alert catalog */}
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2 px-1">
        What you'll be able to subscribe to
      </div>
      <div className="space-y-2">
        {PREVIEW_ALERTS.map((a) => {
          const Icon = a.icon;
          return (
            <div
              key={a.label}
              className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] p-3"
              data-testid={`alerts-preview-${a.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center border border-white/10 shrink-0"
                style={{ background: `${a.color}14` }}
              >
                <Icon className="w-4 h-4" style={{ color: a.color }} strokeWidth={2.3} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-white font-medium leading-tight">{a.label}</div>
                <div className="text-[11px] text-zinc-300 leading-snug mt-0.5">{a.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Footer note */}
    <div className="text-[10px] font-mono text-zinc-500 leading-relaxed text-center px-2">
      Standard message + data rates will apply once live · You'll never be
      charged by EVIQ · Cancel any time by replying STOP
    </div>
  </div>
);
